import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teamMemberService } from '../services/teamMemberService';
import toast from 'react-hot-toast';

const TEAM_MEMBERS_KEY = ['companyTeamMembers'];
const PERMISSIONS_KEY = ['companyPermissions'];

export const useTeamMembers = () => {
    const queryClient = useQueryClient();

    const { data: permissionsData, isLoading: isLoadingPermissions, error: permissionsError } = useQuery({
        queryKey: PERMISSIONS_KEY,
        queryFn: async () => {
            const response = await teamMemberService.getPermissions();
            return response.data.data || [];
        },
        staleTime: 1000 * 60 * 10, 
    });

    const { data: teamMembersData, isLoading: isLoadingMembers, error: membersError } = useQuery({
        queryKey: TEAM_MEMBERS_KEY,
        queryFn: async () => {
            const response = await teamMemberService.getTeamMembers();
            return response.data.data || [];
        },
        staleTime: 1000 * 60 * 2,
    });

    const createMutation = useMutation({
        mutationFn: (data) => teamMemberService.createTeamMember(data),
        onSuccess: () => {
            toast.success('Team member created successfully!');
            queryClient.invalidateQueries(TEAM_MEMBERS_KEY);
        },
        onError: (error) => {
            const msg = error.response?.data?.message || error.response?.data?.error || 'Failed to create team member';
            toast.error(msg);
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => teamMemberService.updateTeamMember(id, data),
        onSuccess: () => {
            toast.success('Team member updated successfully!');
            queryClient.invalidateQueries(TEAM_MEMBERS_KEY);
        },
        onError: (error) => {
            const msg = error.response?.data?.message || error.response?.data?.error || 'Failed to update team member';
            toast.error(msg);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => teamMemberService.deleteTeamMember(id),
        onSuccess: () => {
            toast.success('Team member deleted successfully!');
            queryClient.invalidateQueries(TEAM_MEMBERS_KEY);
        },
        onError: (error) => {
            const msg = error.response?.data?.message || error.response?.data?.error || 'Failed to delete team member';
            toast.error(msg);
        },
    });

    return {
        permissions: permissionsData || [],
        teamMembers: teamMembersData || [],
        isLoadingPermissions,
        isLoadingMembers,
        permissionsError,
        membersError,
        createTeamMember: createMutation.mutate,
        updateTeamMember: updateMutation.mutate,
        deleteTeamMember: deleteMutation.mutate,
        isCreating: createMutation.isPending,
        isUpdating: updateMutation.isPending,
        isDeleting: deleteMutation.isPending,
        refetchMembers: () => queryClient.invalidateQueries(TEAM_MEMBERS_KEY),
        refetchPermissions: () => queryClient.invalidateQueries(PERMISSIONS_KEY),
    };
};

